import { inject, Injectable } from '@angular/core';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { TenantLocalService } from './tenantlocal.service';
import { Select } from 'src/app/core/contracts/models/form-input.model';
import { Api } from '../../contracts/classes/api.class';
import { CreatePlatformDTO, Platform, UpdatePlatformDTO } from '../../contracts/models/platform';
import { PlatformMedia } from '../../contracts/models/medio-plataformas';

@Injectable({ providedIn: 'root' })
export class PlatformService extends Api {

    platformsBs: BehaviorSubject<Platform[]> = new BehaviorSubject<Platform[]>([]);

    private tenantLocal = inject(TenantLocalService);
    private cache$: Observable<any> | null = null;
    private cacheTenant: string | null = null;

    public getAll() {
        return this.http.get(`${this.url}/plataformas`);
    }

    /**
     * Igual que getAll(), pero la respuesta se guarda en memoria por tenant.
     * Se invalida al crear, editar o eliminar plataformas.
     */
    public getAllCached(): Observable<any> {
        const tenant = this.tenantLocal.tenantSlug;
        if (!this.cache$ || this.cacheTenant !== tenant) {
            this.cacheTenant = tenant;
            this.cache$ = this.getAll().pipe(
                catchError(error => {
                    this.clearCache();
                    return throwError(() => error);
                }),
                shareReplay(1)
            );
        }
        return this.cache$;
    }

    public clearCache(): void {
        this.cache$ = null;
    }

    create(data: CreatePlatformDTO) {
        return this.http.post(`${this.url}/plataformas`, data).pipe(tap(() => this.clearCache()));
    }

    update(data: UpdatePlatformDTO) {
        return this.http.put(`${this.url}/plataformas/${data.id}`, data).pipe(tap(() => this.clearCache()));
    }

    delete(id: number) {
        return this.http.delete(`${this.url}/plataformas/${id}`).pipe(tap(() => this.clearCache()));
    }

    getAllMedioPlataformas() {
        return this.http.get(`${this.url}/medioPlataformas`);
    }

    getAllMedioPlataformasId(id) {
        return this.http.get(`${this.url}/medioPlataformas/${id}`);
    }

    createMedioPlataformas(data: PlatformMedia) {
        return this.http.post(`${this.url}/medioPlataformas`, data);
    }

    /** Registra varias plataformas de un medio en una sola transacción. */
    createManyMedioPlataformas(medioId: number, plataformas: PlatformMedia[]) {
        return this.http.post(`${this.url}/medioPlataformas/bulk`, { medio_id: medioId, plataformas });
    }

    updateMedioPlataformas(data: PlatformMedia) {
        return this.http.put(`${this.url}/medioPlataformas/${data.id}`, data);
    }

    deleteMedioPlataformas(id: number) {
        return this.http.delete(`${this.url}/medioPlataformas/${id}`);
    }

    getListByMedio(id: number) {
        return this.http.get(`${this.url}/medioPlataformas/data/getListByMedio/${id}`);
    }

    getPlatforms() {
        return this.platformsBs.getValue();
    }

    updatePlatforms(platforms: Platform[]) {
        this.platformsBs.next(platforms);
    }

    /**
     * @description Observable con la lista de plataformas
     * @method GET
     * @version APIv2.0
     * @returns 
     */
    public all(): Observable<Platform[]> {
        return this.http.get<Platform[]>(`${this.urlv2}/plataformas`);
    }

    /** @version APIv2.0 */
    public allToSelect(): Observable<Select[]> {
        return this.all().pipe(
            map(platforms => platforms.map(platform => ({
                id: platform.id,
                label: platform.descripcion
            })))
        )
    }
}
